import Button from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import PostCard from '@src/components/Post/PostCard/PostCard';
import { isMobile, isTablet } from '@src/utils/utils';
import React, { FC, Fragment, memo, useEffect, useMemo, useState } from 'react'; // Added useState, useMemo
import { useTranslation } from 'react-i18next';
import { usePaginatedPosts } from '@src/store/posts/hooks/usePaginatedPosts';
import EmptyState from './EmptyState';
import MainPostCard from './MainPost';
import AdvertisingSlot from '@src/components/Ads/AdvertisingSlot';
import { getTokenIcon } from '@src/model/token/helpers';

interface PostsTabPartnerPostsProps {
    tokenName: string;
    flairId?: string;
    className?: string;
}

const BATCH_SIZE = 6; // Define the batch size
const PostsTabPartnerPosts: FC<PostsTabPartnerPostsProps> = props => {
    const { tokenName, flairId, className = '' } = props;
    const { postIds, loadMore, hasMore, loadStatus } = usePaginatedPosts({ type: 'partner', tokenName, flairId, fetchLimit: 12 });
    const { postIds: pinnedPostsIds, loadMore: loadPinnedPosts } = usePaginatedPosts({ type: 'pinned', tokenName });

    const { t } = useTranslation();
    // State to control how many posts (besides the main one) are displayed
    const [displayLimit, setDisplayLimit] = useState<number>(BATCH_SIZE);

    // --- Combine Posts ---
    // If flairId is provided, do no combine pinned posts
    const combinedPosts = flairId ? postIds : Array.from(new Set([...pinnedPostsIds, ...postIds]));
    const mainPostId = combinedPosts[0];
    const remainingPosts = combinedPosts.length > 0 ? combinedPosts.slice(1) : [];

    // Posts to actually render in the grid (excluding the main post)
    const postsToDisplay = useMemo(() => remainingPosts.slice(0, displayLimit), [remainingPosts, displayLimit]);

    useEffect(() => {
        if (pinnedPostsIds.length > 0 || flairId) return;
        loadPinnedPosts();
    }, [flairId, tokenName]);

    useEffect(() => {
        if (postIds.length > 0) return; // No need to load more if we already have posts
        if (loadStatus === 'loading') return; // No need to load more if we are already loading
        loadMore();
    }, [flairId]);

    const handleLoadMore = () => {
        const newLimit = displayLimit + BATCH_SIZE;

        if (newLimit > remainingPosts.length && hasMore && loadStatus !== 'loading')
            loadMore();

        setDisplayLimit(newLimit);
    };

    function renderBetweenPostsAd(i = 0) {
        return <AdvertisingSlot key={i} index={i} location="mid_center" tokenName={tokenName} />;
    }

    function renderLoadMoreButton() {
        const canShowMoreLocally = displayLimit < remainingPosts.length;
        const showButton = canShowMoreLocally || hasMore;

        if (!showButton) return null;

        return (
            <Button
                isLoading={loadStatus === 'loading'}
                size="md"
                className="w-full"
                onClick={handleLoadMore}
            >
                <Text>{t('partnerPostList.loadMoreButton')}</Text>
            </Button>
        );
    }

    // Renders a single post card in the grid
    function renderGridPost(postId: string, index: number) {
        const displayIndex = index + 1;
        return (
            <Fragment key={postId}>
                <PostCard
                    fallbackImage={getTokenIcon(tokenName)}
                    tokenName={tokenName}
                    className={cn('bg-transparent dark:bg-transparent')}
                    postId={postId}
                />
                {/* Render an after the 2nd post card in the grid for tablet */}
                {isTablet() && displayIndex === 1 && <div className="hidden md:block lg:hidden">{renderBetweenPostsAd(displayIndex)}</div>}
                {/* Render an ad every 3rd *rendered* post card in the grid  */}
                {isMobile() && !isTablet() && displayIndex % 3 === 0 && <div className="lg:hidden">{renderBetweenPostsAd(displayIndex)}</div>}
            </Fragment>
        );
    }

    // Renders the main (first) post
    function renderMainPost() {
        if (!mainPostId && loadStatus === 'success') return;
        return <MainPostCard isLoading={loadStatus === 'loading'} key={mainPostId} tokenName={tokenName} postId={mainPostId} />;
    }

    function renderEmptyState() {
        if (loadStatus !== 'success' || combinedPosts.length > 0) return null;
        return <EmptyState type="partner" />;
    }

    function renderPostsGrid() {
        return (
            <div className="grid gap-x-5 gap-y-4 md:grid-cols-2 lg:grid-cols-3">
                {renderMainPost()}
                {renderDesktopMidRightAd()}
                {postsToDisplay.map(renderGridPost)}
            </div>
        );
    }

    function renderDesktopMidRightAd() {
        if (isMobile()) return null;
        if (postsToDisplay.length === 0) return null;
        return <AdvertisingSlot className="hidden lg:block" location="mid_right" tokenName={tokenName} />;
    }

    return (
        <div className={cn('space-y-4', className)}>
            {renderEmptyState()}
            {renderPostsGrid()}
            {renderLoadMoreButton()}
        </div>
    );
};

export default memo(PostsTabPartnerPosts);